import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type ItAssetCredentialInput,
  type ItAssetFilters,
  type ItAssetInput,
  type ItAssetStatus,
  type ItAssetUpdateInput,
  itAssetsApi,
} from '@/services';

export const itAssetsKeys = {
  all: ['admin', 'it-assets'] as const,
  list: (f: ItAssetFilters) => ['admin', 'it-assets', 'list', f] as const,
  summary: () => ['admin', 'it-assets', 'summary'] as const,
  detail: (id: string) => ['admin', 'it-assets', 'detail', id] as const,
  modelPhotos: () => ['admin', 'it-assets', 'model-photos'] as const,
};

export function useItAssetModelPhotosQuery(enabled = true) {
  return useQuery({ queryKey: itAssetsKeys.modelPhotos(), queryFn: () => itAssetsApi.modelPhotos(), enabled });
}

export function useItAssetsQuery(filters: ItAssetFilters) {
  return useQuery({
    queryKey: itAssetsKeys.list(filters),
    queryFn: () => itAssetsApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useItAssetSummaryQuery() {
  return useQuery({ queryKey: itAssetsKeys.summary(), queryFn: () => itAssetsApi.summary() });
}

export function useItAssetQuery(id: string | undefined) {
  return useQuery({
    queryKey: itAssetsKeys.detail(id ?? ''),
    queryFn: () => itAssetsApi.get(id as string),
    enabled: !!id,
  });
}

function useItAssetMutation<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: itAssetsKeys.all }) });
}

export const useCreateItAssetMutation = () => useItAssetMutation((data: ItAssetInput) => itAssetsApi.create(data));

export const useUpdateItAssetMutation = () =>
  useItAssetMutation(({ id, data }: { id: string; data: ItAssetUpdateInput }) => itAssetsApi.update(id, data));

export const useAssignItAssetMutation = () =>
  useItAssetMutation(
    ({ id, data }: { id: string; data: { userId: string | null; roomId: string | null; note?: string } }) =>
      itAssetsApi.assign(id, data)
  );

export const useChangeItAssetStatusMutation = () =>
  useItAssetMutation(({ id, data }: { id: string; data: { status: ItAssetStatus; note?: string } }) =>
    itAssetsApi.changeStatus(id, data)
  );

export const useDeleteItAssetMutation = () => useItAssetMutation((id: string) => itAssetsApi.remove(id));

export const useUploadItAssetPhotoMutation = () =>
  useItAssetMutation(({ id, file }: { id: string; file: File }) => itAssetsApi.uploadPhoto(id, file));

export const useRemoveItAssetPhotoMutation = () => useItAssetMutation((id: string) => itAssetsApi.removePhoto(id));

export const useBulkItAssetPhotosMutation = () => useItAssetMutation((files: File[]) => itAssetsApi.bulkPhotos(files));

export const useCreateItAssetCredentialMutation = () =>
  useItAssetMutation(({ id, data }: { id: string; data: ItAssetCredentialInput }) =>
    itAssetsApi.createCredential(id, data)
  );

export const useUpdateItAssetCredentialMutation = () =>
  useItAssetMutation(({ id, credId, data }: { id: string; credId: string; data: Partial<ItAssetCredentialInput> }) =>
    itAssetsApi.updateCredential(id, credId, data)
  );

export const useDeleteItAssetCredentialMutation = () =>
  useItAssetMutation(({ id, credId }: { id: string; credId: string }) => itAssetsApi.removeCredential(id, credId));

export const useSetItAssetModelPhotoMutation = () =>
  useItAssetMutation(({ brand, model, file }: { brand: string | null; model: string; file: File }) =>
    itAssetsApi.setModelPhoto(brand, model, file)
  );

export const useRemoveItAssetModelPhotoMutation = () =>
  useItAssetMutation((photoId: string) => itAssetsApi.removeModelPhoto(photoId));
